import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useLang } from '../i18n/context';
import SEO from '../components/SEO';
import NotFound from './NotFound';
import { Container, Reveal } from '../components/Reveal';
import ArticleCard from '../components/ArticleCard';
import { WhatsAppButton, BookButton } from '../components/CTAs';
import { breadcrumbSchema, dentistPersonSchema } from '../lib/schema';
import { fetchArticles } from '../lib/api';
import { DOCTORS, getDoctor } from '../data/doctors';

export default function DoctorPage() {
    const { slug } = useParams();
    const { t, lang } = useLang();
    const doctor = getDoctor(slug);
    const [articles, setArticles] = useState([]);

    useEffect(() => {
        if (doctor) {
            fetchArticles().then((all) => setArticles(all.filter((a) => a.author === doctor.name).slice(0, 4))).catch(() => {});
        }
    }, [doctor]);

    if (!doctor) return <NotFound />;
    const isIrma = doctor.key === 'irma';
    const metaTitle = isIrma ? t.doctors.metaTitleIrma : t.doctors.metaTitleAriandi;
    const metaDesc = isIrma ? t.doctors.metaDescIrma : t.doctors.metaDescAriandi;
    const roles = isIrma ? t.doctors.irmaRoles : t.doctors.ariandiRoles;

    return (
        <>
            <SEO
                title={metaTitle}
                description={metaDesc}
                path={`/doctors/${doctor.slug}`}
                lang={lang}
                jsonLd={[
                    dentistPersonSchema(doctor),
                    breadcrumbSchema(lang, [
                        { name: t.nav.home, path: '/' },
                        { name: t.nav.doctors, path: '/doctors/dr-irma-ariandi' },
                        { name: doctor.name, path: `/doctors/${doctor.slug}` },
                    ]),
                ]}
            />
            <section className="relative overflow-hidden pt-28 md:pt-36" data-testid="doctor-profile">
                <div className="pointer-events-none absolute -right-32 -top-24 h-96 w-96 rounded-full bg-peri-200/70" aria-hidden="true" />
                <Container className="relative">
                    <div className="grid items-start gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
                        <Reveal>
                            <img src={doctor.photo} alt={doctor.name} className="aspect-[4/5] w-full rounded-3xl border border-peri-200 object-cover" data-testid="doctor-photo" />
                        </Reveal>
                        <Reveal delay={0.1}>
                            <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-gold-700">{t.doctors.profile}</p>
                            <h1 className="mt-4 font-serif text-4xl font-semibold leading-tight tracking-tight text-ink md:text-6xl">{doctor.name}</h1>
                            <p className="mt-3 text-lg font-semibold text-gold-700">{doctor.creds}</p>
                            <p className="mt-2 font-serif text-xl italic text-ink-muted">{roles}</p>

                            {isIrma && (
                                <div className="mt-8 rounded-3xl border border-peri-200 bg-peri-50 p-7">
                                    <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gold-700">Coffee With Dr. Irma</p>
                                    <p className="mt-3 text-[15px] leading-relaxed text-ink-body">{t.doctors.irmaColumnist}</p>
                                    <Link to={`/dental/${lang}/kolum`} className="mt-4 inline-block text-sm font-semibold text-gold-700 underline underline-offset-4" data-testid="doctor-irma-kolum-link">
                                        {t.nav.kolum} →
                                    </Link>
                                </div>
                            )}

                            <div className="mt-8 space-y-6">
                                <div>
                                    <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gold-700">{t.doctors.quals}</p>
                                    <p className="mt-2 text-[15px] text-ink-body">{doctor.creds}</p>
                                </div>
                                <div>
                                    <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gold-700">{t.doctors.profile} — bio</p>
                                    <p className="mt-2 text-sm italic text-ink-subtle">{t.doctors.pendingBio}</p>
                                </div>
                                <div>
                                    <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gold-700">{t.doctors.interests}</p>
                                    <p className="mt-2 text-sm italic text-ink-subtle">{t.doctors.pendingInterests}</p>
                                </div>
                                <div>
                                    <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gold-700">{t.doctors.philosophy}</p>
                                    <p className="mt-2 text-sm italic text-ink-subtle">{t.doctors.pendingPhilosophy}</p>
                                </div>
                            </div>

                            <div className="mt-9 flex flex-wrap gap-3">
                                <WhatsAppButton label={t.cta.whatsappLong} source={`doctor-${doctor.key}`} />
                                <BookButton label={`${t.doctors.cta} ${doctor.name}`} lang={lang} source={`doctor-${doctor.key}`} />
                            </div>
                        </Reveal>
                    </div>
                </Container>
            </section>

            {articles.length > 0 && (
                <section className="py-16 md:py-24" data-testid="doctor-articles">
                    <Container>
                        <Reveal>
                            <h2 className="mb-8 font-serif text-2xl font-semibold text-ink md:text-3xl">
                                {t.doctors.articlesBy} {doctor.name}
                            </h2>
                        </Reveal>
                        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
                            {articles.map((a, i) => (
                                <Reveal key={a.slug} delay={i * 0.06} className="h-full">
                                    <ArticleCard article={a} />
                                </Reveal>
                            ))}
                        </div>
                    </Container>
                </section>
            )}

            <section className="bg-peri-50 py-14">
                <Container>
                    <Reveal>
                        <div className="flex flex-wrap items-center justify-between gap-6">
                            <p className="font-serif text-2xl font-semibold text-ink">{t.nav.doctors}</p>
                            <div className="flex flex-wrap gap-3">
                                {DOCTORS.filter((d) => d.slug !== doctor.slug).map((d) => (
                                    <Link key={d.slug} to={`/dental/${lang}/doctors/${d.slug}`} className="rounded-full border border-peri-300 bg-white px-5 py-2.5 text-sm font-semibold text-gold-700 hover:bg-peri-50" data-testid={`doctor-switch-${d.slug}`}>
                                        {d.name} →
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </Reveal>
                </Container>
            </section>
        </>
    );
}
